/**
 * Phase 20 wizard — Step 5: the estimate.
 *
 * On entry the shell asks Stella to compile the estimate (the same
 * `askForEstimate` turn the workspace already fires). The existing
 * `EstimateSnapshotPanel` renders the result — including the versioned
 * non-binding disclosure — so pricing/display are unchanged (FR-011).
 */

import { useEffect, useRef } from "react";

import type {
  EstimateStreamPayload,
  PackageAddOn,
} from "@starz/shared/api/types.gen";
import { EstimateSnapshotPanel } from "@/components/chat/EstimateSnapshotPanel";
import { PackageAddOns } from "./PackageAddOns";

interface Props {
  /** Wrapped snapshot payload (`{ kind: "snapshot", snapshot }`) or null. */
  snapshotPayload: EstimateStreamPayload | null;
  /** Ask Stella to compile the estimate (called once when none exists). */
  onRequestEstimate: () => void;
  /** Phase 21 — add-ons offered by the applied package (never in the total). */
  addOns?: PackageAddOn[];
  /** Material ids already selected, so an added add-on stops being offered. */
  selectedMaterialIds?: string[];
  /** Record an add-on as a selection. */
  onAddAddOn?: (addOn: PackageAddOn) => void;
  streaming?: boolean;
  disabled?: boolean;
}

export function EstimateStep({
  snapshotPayload,
  onRequestEstimate,
  addOns = [],
  selectedMaterialIds = [],
  onAddAddOn,
  streaming = false,
  disabled = false,
}: Props) {
  const hasSnapshot = snapshotPayload != null && snapshotPayload.kind !== "missing_inputs";

  // Ask Stella to compile the estimate exactly once — never in a loop.
  const requestedRef = useRef(false);
  useEffect(() => {
    if (!requestedRef.current && !hasSnapshot && !streaming && !disabled) {
      requestedRef.current = true;
      onRequestEstimate();
    }
  }, [hasSnapshot, streaming, disabled, onRequestEstimate]);

  if (hasSnapshot && snapshotPayload) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-warm-gray">
          Here's your preliminary estimate. It's not binding until your contractor reviews it.
        </p>
        <EstimateSnapshotPanel payload={snapshotPayload} />
        {onAddAddOn && (
          <PackageAddOns
            addOns={addOns}
            selectedMaterialIds={selectedMaterialIds}
            onAdd={onAddAddOn}
            disabled={disabled || streaming}
          />
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-starz-gold/20 bg-starz-cream/30 p-6 text-sm text-warm-gray">
      {streaming ? "Stella is putting your estimate together…" : "Preparing your estimate…"}
    </div>
  );
}
